import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import mascot from "@/assets/mascot.png";
import { useLang } from "@/hooks/use-language";
import { getPublicParentArticle, type ParentArticleFull } from "@/lib/parents.functions";
import { ArrowLeft, Clock } from "lucide-react";

export const Route = createFileRoute("/parents/$slug")({
  loader: async ({ params }) => {
    const article = await getPublicParentArticle({ data: { slug: params.slug } });
    if (!article) throw notFound();
    return article;
  },
  head: ({ loaderData }) => {
    const a = loaderData as ParentArticleFull | undefined;
    if (!a) {
      return {
        meta: [
          { title: "Article not found — Jozori" },
          { name: "robots", content: "noindex" },
        ],
      };
    }
    const title = `${a.title_en} | ${a.title_ar} — Jozori`;
    const desc =
      a.description_en ?? a.description_ar ?? "Parenting guide from Jozori | جذوري";
    const meta: Array<Record<string, string>> = [
      { title },
      { name: "description", content: desc },
      { property: "og:type", content: "article" },
      { property: "og:title", content: title },
      { property: "og:description", content: desc },
      { property: "og:url", content: `https://jozori.com/parents/${a.slug}` },
    ];
    if (a.cover_url) {
      meta.push({ property: "og:image", content: a.cover_url });
      meta.push({ name: "twitter:image", content: a.cover_url });
    }
    return {
      meta,
      links: [{ rel: "canonical", href: `https://jozori.com/parents/${a.slug}` }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: a.title_en,
            alternativeHeadline: a.title_ar,
            description: desc,
            image: a.cover_url ?? undefined,
            datePublished: a.published_at,
            author: { "@type": "Organization", name: "Jozori" },
            publisher: {
              "@type": "Organization",
              name: "Jozori | جذوري",
              url: "https://jozori.com",
            },
            inLanguage: ["ar", "en"],
          }),
        },
      ],
    };
  },
  notFoundComponent: () => {
    const { lang, dir } = useLang();
    const isAr = lang === "ar";
    return (
      <div dir={dir} className="min-h-screen bg-background flex flex-col items-center justify-center px-4 text-center">
        <h1 className="text-3xl font-black mb-2">{isAr ? "المقال غير موجود" : "Article not found"}</h1>
        <Link to="/parents" className="text-primary font-bold underline">
          {isAr ? "العودة إلى ركن الأهل" : "Back to Parents Hub"}
        </Link>
      </div>
    );
  },
  errorComponent: ({ error }) => (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center px-4 text-center">
      <p className="text-foreground/70 mb-3">{error.message}</p>
      <Link to="/parents" className="text-primary font-bold underline">Back to Parents Hub</Link>
    </div>
  ),
  component: ArticlePage,
});

function ArticlePage() {
  const a = Route.useLoaderData() as ParentArticleFull;
  const { lang, dir, toggle } = useLang();
  const isAr = lang === "ar";
  const title = isAr ? a.title_ar : a.title_en;
  const body = isAr ? a.body_ar : a.body_en;
  const desc = isAr ? a.description_ar : a.description_en;

  return (
    <div dir={dir} className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-30 border-b border-clay/30 bg-background/80 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-3">
          <Link to="/" className="flex items-center gap-2 font-display-en text-lg font-black">
            <img src={mascot} alt="" className="size-8 rounded-full ring-2 ring-primary/30" />
            <span>Jozori</span>
          </Link>
          <button
            onClick={toggle}
            className="rounded-full border border-clay/40 bg-card px-3 py-1.5 text-xs font-bold hover:bg-clay/20"
          >
            {isAr ? "English" : "العربية"}
          </button>
        </div>
      </header>

      <article className="mx-auto max-w-3xl px-4 py-10">
        <Link
          to="/parents"
          className="inline-flex items-center gap-2 text-sm font-bold text-foreground/60 hover:text-foreground mb-6"
        >
          <ArrowLeft className="size-4 rtl:rotate-180" />
          {isAr ? "كل المقالات" : "All articles"}
        </Link>

        <h1
          className={`text-3xl md:text-5xl font-black leading-tight ${
            isAr ? "font-display-ar" : "font-display-en"
          }`}
        >
          {title}
        </h1>

        <div className="mt-3 flex items-center gap-4 text-xs font-bold text-foreground/50">
          {a.reading_minutes ? (
            <span className="inline-flex items-center gap-1">
              <Clock className="size-3" />
              {a.reading_minutes} {isAr ? "د قراءة" : "min read"}
            </span>
          ) : null}
          {a.published_at && (
            <time dateTime={a.published_at}>
              {new Date(a.published_at).toLocaleDateString(isAr ? "ar" : "en", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </time>
          )}
        </div>

        {a.cover_url && (
          <div className="mt-6 aspect-[16/9] overflow-hidden rounded-3xl bg-clay/20">
            <img src={a.cover_url} alt={title} className="size-full object-cover" />
          </div>
        )}

        {desc && (
          <p className="mt-6 text-lg text-foreground/80 leading-relaxed font-medium border-s-4 border-secondary ps-4">
            {desc}
          </p>
        )}

        {body ? (
          <div className="mt-8 prose prose-lg max-w-none text-foreground/90 whitespace-pre-wrap leading-relaxed">
            {body}
          </div>
        ) : (
          <p className="mt-8 text-foreground/60 italic">
            {isAr ? "المحتوى الكامل قريبًا." : "Full content coming soon."}
          </p>
        )}

        <div className="mt-12 rounded-3xl border-2 border-primary/30 bg-gradient-to-br from-primary/10 to-secondary/10 p-6 text-center">
          <p className={`text-lg font-black mb-2 ${isAr ? "font-display-ar" : "font-display-en"}`}>
            {isAr
              ? "جوزوري — رحلة تعلّم ثنائية اللغة لطفلك"
              : "Jozori — a bilingual learning journey for your child"}
          </p>
          <p className="text-sm text-foreground/70 mb-4">
            {isAr
              ? "قصص، فيديوهات، أنشطة وعادات يومية بالعربية والإنجليزية — مبنية على القيم."
              : "Stories, videos, activities and daily habits in Arabic and English — built around values."}
          </p>
          <Link
            to="/login"
            className="inline-flex items-center justify-center rounded-2xl bg-primary text-primary-foreground px-6 py-3 font-bold text-sm hover:opacity-90 transition"
          >
            {isAr ? "ابدأ مجانًا" : "Start free"}
          </Link>
        </div>
      </article>
    </div>
  );
}
